import {
  LuMail,
  LuLayoutTemplate,
  LuHistory,
  LuSend,
  LuRotateCcw,
  LuCheck,
  LuX,
  LuCreditCard,
  LuUsers,
  LuPackage,
  LuClock,
  LuEye,
  LuPencil,
  LuFileText,
  LuLoader,
  LuInfo,
  LuUser
} from "react-icons/lu";

import React, { useState, useEffect } from "react";
import { db } from "@lib/firebase";
import { collection, getDocs, doc, setDoc, getDoc, serverTimestamp, getCountFromServer, limit, query, where } from "firebase/firestore";
import { useNotification } from "../../common/NotificationContext";
import { useAuth } from "../../context/AuthContext";
import { InputField, Button } from "@cakrawalajakarta/shared-components";

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

// ==================== EMAIL TEMPLATES ====================
const EMAIL_TEMPLATES = {
  success: {
    name: "Success",
    icon: <LuCheck />,
    color: "green",
    subject: "Payment Successful - JOTR 2026",
    variables: ["{{name}}", "{{orderId}}", "{{category}}", "{{price}}", "{{registrationInfo}}"],
    body: `Hello {{name}},

Thank you! Your payment for Jakarta Open Trail Run 2026 has been successfully confirmed.

<strong>Registration Details:</strong>{{registrationInfo}}
Keep this email as proof of your registration. Further information regarding racepack collection will be sent closer to the event.

See you at the start line! 🏃‍♂️`,
    matchKeywords: ["payment successful", "completed", "payment success", "e-ticket", "receipt"],
  },
  pending: {
    name: "Received",
    icon: <LuClock />,
    color: "yellow",
    subject: "Registration Received - JOTR 2026",
    variables: ["{{name}}", "{{orderId}}", "{{category}}", "{{status}}", "{{registrationInfo}}"],
    body: `Hello {{name}},

We have received your registration for Jakarta Open Trail Run 2026.

<strong>Registration Details:</strong>{{registrationInfo}}
Your data is currently being <b>verified by the admin</b>. Please wait for the next confirmation email before making a payment.

Please ensure the data you submitted is correct. If there are any errors, please contact our admin immediately.`,
    matchKeywords: ["registration received", "waiting for admin", "received", "registration successfully sent"],
  },
  approved: {
    name: "Approved",
    icon: <LuCheck />,
    color: "green",
    subject: "Registration Approved - JOTR 2026",
    variables: ["{{name}}", "{{orderId}}", "{{category}}", "{{registrationInfo}}"],
    body: `Hello {{name}},

Congratulations! Your registration for <b>Jakarta Open Trail Run 2026</b> has been <b>APPROVED</b> by the admin.

<strong>Registration Details:</strong>{{registrationInfo}}
The next step is to complete the payment to secure your slot.

<div style="text-align: center; margin-top: 30px;">
  <a href="https://jakartaopentrailrun.com/myregistration?order_id={{orderId}}" class="cta-button">Pay Now</a>
</div>

<p style="font-size: 13px; color: #666; margin-top: 15px;">Ignore this message if you have already made the payment.</p>`,
    matchKeywords: ["registration approved", "approved", "congratulations", "qualified"],
  },
  rejected: {
    name: "Rejected",
    icon: <LuX />,
    color: "red",
    subject: "Registration Rejected - JOTR 2026",
    variables: ["{{name}}", "{{orderId}}", "{{category}}", "{{registrationInfo}}"],
    body: `Hello {{name}},

We are sorry, your registration for <b>Jakarta Open Trail Run 2026</b> could not be approved.

<strong>Registration Details:</strong>{{registrationInfo}}
Please correct your data or choose another suitable category, then re-register.`,
    matchKeywords: ["registration rejected", "rejected", "declined"],
  },
  payment_instruction: {
    name: "Payment",
    icon: <LuCreditCard />,
    color: "blue",
    subject: "Payment Instruction - JOTR 2026",
    variables: ["{{name}}", "{{orderId}}", "{{paymentInfo}}", "{{price}}", "{{expiryTime}}"],
    body: `Hello {{name}},

Thank you for registering. Please complete your payment using the details below:

<strong>Payment Details:</strong>{{paymentInfo}}
Please complete the payment before the specified deadline.

<div style="text-align: center; margin-top: 30px;">
  <a href="https://jakartaopentrailrun.com/myregistration" class="cta-button">Check Payment Status</a>
</div>`,
    matchKeywords: ["payment instruction", "invoice", "payment"],
  },
  category_upgrade_payment_instruction: {
    name: "Upgrade Payment",
    icon: <LuCreditCard />,
    color: "blue",
    subject: "Category Upgrade Payment - JOTR 2026",
    variables: ["{{name}}", "{{orderId}}", "{{sourceOrderId}}", "{{fromCategory}}", "{{toCategory}}", "{{priceDifference}}", "{{upgradeFee}}", "{{amountDue}}", "{{paymentInfo}}", "{{expiryTime}}"],
    body: `Hello {{name}},

Your category upgrade request for JOTR Cianjur Series 2026 has been received.

<strong>Upgrade Details:</strong>{{paymentInfo}}

Please complete the payment before {{expiryTime}}. If payment is not completed before the deadline, your registration will remain in the original category.

<div style="text-align: center; margin-top: 30px;">
  <a href="https://jakartaopentrailrun.com/myregistration" class="cta-button">Open My Registration</a>
</div>`,
    matchKeywords: ["category upgrade payment", "upgrade invoice", "upgrade payment"],
  },
  category_upgrade_success: {
    name: "Upgrade Success",
    icon: <LuCheck />,
    color: "green",
    subject: "Category Upgrade Confirmed - JOTR 2026",
    variables: ["{{name}}", "{{orderId}}", "{{sourceOrderId}}", "{{fromCategory}}", "{{toCategory}}", "{{amountPaid}}", "{{upgradeInfo}}"],
    body: `Hello {{name}},

Your category upgrade payment for JOTR Cianjur Series 2026 has been confirmed.

<strong>Upgrade Details:</strong>{{upgradeInfo}}

Your registration is now listed in the new category. Please check My Registration for the latest details.

<div style="text-align: center; margin-top: 30px;">
  <a href="https://jakartaopentrailrun.com/myregistration" class="cta-button">View My Registration</a>
</div>`,
    matchKeywords: ["category upgrade confirmed", "upgrade success", "category upgrade success"],
  },
  expired: {
    name: "Expired",
    icon: <LuHistory />,
    color: "red",
    subject: "Payment Expired - JOTR 2026",
    variables: ["{{name}}", "{{orderId}}", "{{category}}", "{{registrationInfo}}"],
    body: `Hello {{name}},

Your payment time has run out. Your order has been automatically cancelled by the system.

<strong>Registration Details:</strong>{{registrationInfo}}
Please re-register if you are still interested in participating in this event.`,
    matchKeywords: ["payment expired", "expired", "time out", "late payment"],
  },
  racepack: {
    name: "Racepack",
    icon: <LuPackage />,
    color: "orange",
    subject: "Racepack Collection - JOTR 2026",
    variables: ["{{name}}", "{{orderId}}", "{{category}}", "{{bibNumber}}", "{{collectionInfo}}"],
    body: `Hello {{name}},
    
Your racepack is ready to be collected.

<strong>Collection Details:</strong>{{collectionInfo}}

<strong>What to Bring:</strong>
• This E-Ticket (digital/print)
• Identity Card (KTP/SIM/Passport)
• Authorization Letter (if represented)
• Copy of ID of the person giving authority (if represented)

<strong>Racepack Contents:</strong>
• Official JOTR 2026 Jersey
• BIB Number: {{bibNumber}}
• Goodie bag

Don't forget to bring a bag for your racepack!

Best regards,
<strong>JOTR CIANJUR SERIES 2026 Team</strong>`,
    matchKeywords: ["racepack", "race collection"],
  },
  shuttle_success: {
    name: "Shuttle Success",
    icon: <LuCheck />,
    color: "green",
    subject: "Shuttle Payment Successful - JOTR 2026",
    variables: ["{{name}}", "{{orderId}}", "{{tripType}}", "{{shuttleInfo}}", "{{price}}"],
    body: `Hello {{name}},
    
Thank you! Your shuttle payment for <b>JOTR CIANJUR SERIES 2026</b> has been successfully confirmed.

<strong>Shuttle Booking Details:</strong>{{shuttleInfo}}

Save this email as valid proof of booking. Make sure you arrive at the departure location at least <b>30 minutes before the schedule</b>.`,
    matchKeywords: ["shuttle payment successful", "shuttle success", "shuttle completed"],
  },
  shuttle_payment_instruction: {
    name: "Shuttle Payment",
    icon: <LuCreditCard />,
    color: "blue",
    subject: "Shuttle Payment Instruction - JOTR 2026",
    variables: ["{{name}}", "{{orderId}}", "{{tripType}}", "{{shuttleDetail}}", "{{paymentInfo}}", "{{price}}", "{{expiryTime}}"],
    body: `Hello {{name}},
    
Thank you for booking the <b>JOTR CIANJUR SERIES 2026</b> shuttle. Please complete your payment before the deadline.

<strong>Trip Details:</strong>{{shuttleDetail}}

<strong>Payment Details:</strong>{{paymentInfo}}

If payment is not made before {{expiryTime}}, your order will be automatically cancelled by the system.

<div style="text-align: center; margin-top: 30px;">
  <a href="https://jakartaopentrailrun.com/shuttle" class="cta-button">Check Payment Status</a>
</div>`,
    matchKeywords: ["shuttle payment instruction", "shuttle invoice"],
  },
  shuttle_expired: {
    name: "Shuttle Expired",
    icon: <LuHistory />,
    color: "red",
    subject: "Shuttle Booking Expired - JOTR 2026",
    variables: ["{{name}}", "{{orderId}}", "{{tripType}}", "{{shuttleInfo}}"],
    body: `Hello {{name}},
    
The payment time for your shuttle booking has expired. Your order has been automatically cancelled.

<strong>Previous Booking Details:</strong>{{shuttleInfo}}

If you still need shuttle facilities, please re-book through the website as long as the quota is available.

<div style="text-align: center; margin-top: 30px;">
  <a href="https://jakartaopentrailrun.com/shuttle" class="cta-button">Re-book Shuttle</a>
</div>`,
    matchKeywords: ["shuttle booking expired", "shuttle expired"],
  },
};

const generateTemplateHtml = (recipientName, content, type = "default") => {
  return `
<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>JOTR CIANJUR SERIES 2026</title>
  <style>
    body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif; margin: 0; padding: 0; background-color: #ffffff; color: #333333; }
    .email-container { max-width: 600px; margin: 0 auto; background-color: #ffffff; padding: 20px; }
    
    /* Header */
    .header { padding-bottom: 20px; border-bottom: 2px solid #eeeeee; margin-bottom: 30px; text-align: center; }
    .header h1 { margin: 0; color: #111111; font-size: 24px; font-weight: 800; text-transform: uppercase; letter-spacing: 1px; }
    
    /* Content */
    .greeting { font-size: 16px; font-weight: 600; margin-bottom: 15px; color: #111111; }
    .text-body { font-size: 15px; line-height: 1.6; color: #444444; margin-bottom: 30px; white-space: pre-wrap; }
    
    /* Table */
    .detail-table { width: 100%; border-collapse: collapse; margin: 8px 0; }
    .detail-table td { padding: 6px 0; border-bottom: 1px dashed #eeeeee; vertical-align: middle; }
    .detail-label { text-align: left; font-size: 14px; color: #666666; font-weight: 500; }
    .detail-value { text-align: right; font-size: 14px; color: #111111; font-weight: 600; }
    
    /* Badges */
    .badge { display: inline-block; padding: 4px 10px; border-radius: 4px; font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.5px; line-height: 1; vertical-align: middle; }
    .badge-pending { background-color: #fff7ed; color: #c2410c; border: 1px solid #ffedd5; }
    .badge-paid { background-color: #f0fdf4; color: #15803d; border: 1px solid #dcfce7; }
    .badge-qualified { background-color: #f0f9ff; color: #0369a1; border: 1px solid #e0f2fe; }
    .badge-rejected { background-color: #fef2f2; color: #b91c1c; border: 1px solid #fee2e2; }
    
    .info-box { background-color: #f8fafc; border-left: 3px solid #3b82f6; padding: 15px; margin: 20px 0; font-size: 14px; color: #475569; }

    .cta-button { display: inline-block; padding: 12px 24px; background-color: #111111; color: #ffffff; text-decoration: none; font-weight: 600; border-radius: 8px; font-size: 14px; }
    
    /* Footer */
    .footer { margin-top: 50px; padding-top: 20px; border-top: 1px solid #f3f4f6; color: #9ca3af; font-size: 13px; text-align: center; }
    .footer a { color: #6b7280; text-decoration: none; }
    .footer p { margin: 5px 0; }
    
    .signature { margin-top: 40px; color: #444444; font-size: 14px; }
    .signature strong { color: #111111; }
  </style>
</head>
<body>
  <div class="email-container">
    <div class="header">
      <h1>JOTR CIANJUR SERIES 2026</h1>
    </div>
    
    <div class="text-body">${content}</div> 
    
    <div class="signature">
      Sincerely,<br>
      <strong>JOTR CIANJUR SERIES 2026 Team</strong>
    </div>
    
    <div class="footer">
      <p>&copy; 2026 JOTR CIANJUR SERIES 2026. All rights reserved.</p>
      <p><a href="https://jakartaopentrailrun.com">jakartaopentrailrun.com</a></p>
    </div>
  </div>
</body>
</html>
  `;
};

const getTemplateNameBySubject = (subject) => {
    if (!subject) return "Unknown";
    const normalize = (s) => s.toLowerCase().trim();
    const subjLower = normalize(subject);

    // Dynamic Matching using Configuration (matchKeywords)
    for (const key in EMAIL_TEMPLATES) {
        const template = EMAIL_TEMPLATES[key];
        
        // 1. Check Keywords (Primary)
        if (template.matchKeywords && Array.isArray(template.matchKeywords)) {
            if (template.matchKeywords.some(keyword => subjLower.includes(keyword))) {
                return template.name;
            }
        }

        // 2. Fallback: Check strictly against template subject phrase
        const tmplSubject = normalize(template.subject);
        if (subjLower.includes(tmplSubject)) {
            return template.name;
        }
    }

    return "Broadcast";
};

export const Emails = () => {
  const [activeTab, setActiveTab] = useState("compose");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  
  // New State for Selection
  const [participants, setParticipants] = useState([]);
  const [filteredParticipants, setFilteredParticipants] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [searchQuery, setSearchQuery] = useState("");
  
  const [loading, setLoading] = useState(true);
  const [showPreview, setShowPreview] = useState(false);
  const [emailLogs, setEmailLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendLimit, setResendLimit] = useState(200);
  const [resendDelayMs, setResendDelayMs] = useState(120);
  const [resendForce, setResendForce] = useState(false);
  
  // Server-side stats
  const [totalLogs, setTotalLogs] = useState(0); 
  const [serverStats, setServerStats] = useState(null); 
  
  // New State for Smart Targeting
  const [broadcastTarget, setBroadcastTarget] = useState("verified"); // Default to verified as requested
  const [targetCounts, setTargetCounts] = useState({ all: 0, verified: 0, pending: 0 });
  const { showNotification } = useNotification();
  const { getIdToken } = useAuth();
  const COLLECTION_NAME = "pendaftaranJOTR2026";

  const fetchEmailLogs = async () => {
    setLogsLoading(true);
    try {
      const token = await getIdToken();
      const response = await fetch(`${API_BASE_URL}/api/email-logs`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const data = await response.json();
      if (response.ok) {
        setEmailLogs(data.logs || []);
        // Capture total from API if available as fallback
        setTotalLogs(data.total || (data.logs || []).length); 
      } else {
        showNotification("error", data.error || "Failed to fetch logs");
      }
    } catch (error) {
      console.error("Error fetching email logs:", error);
      showNotification("error", "Failed to fetch email logs");
    } finally {
      setLogsLoading(false);
    }
  };

  const handleRetryEmailLog = async () => {
    if (!selectedLog?.id) return;

    setRetryLoading(true);
    try {
      const token = await getIdToken();
      const response = await fetch(`${API_BASE_URL}/api/email-logs/${selectedLog.id}/retry`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();

      if (response.ok) {
        showNotification("success", "Retry queued/sent");
        setSelectedLog((prev) => prev ? { ...prev, status: "sent", error: null } : prev);
        setTimeout(() => {
          fetchEmailLogs();
          fetchServerStats();
        }, 1200);
      } else {
        showNotification("error", data.error || "Failed to retry");
      }
    } catch (e) {
      console.error("Retry email log error:", e);
      showNotification("error", "Failed to retry: " + e.message);
    } finally {
      setRetryLoading(false);
    }
  };

  const fetchServerStats = async () => {
    try {
        const token = await getIdToken();
        const response = await fetch(`${API_BASE_URL}/api/email-stats`, {
            headers: { Authorization: `Bearer ${token}` }
        });
        if (response.ok) {
            const data = await response.json();
            console.log("Stats Loaded:", data);
            setServerStats(data);
        }
    } catch (error) {
        console.error("Failed to fetch server stats:", error);
    }
  };

  const handleResendFailedEmails = async () => {
    const parsedLimit = Math.min(Number(resendLimit) || 200, 1000);
    const parsedDelayMs = Math.max(Number(resendDelayMs) || 0, 0);

    if (!confirm(`Resend up to ${parsedLimit} FAILED emails?`)) return;

    setResendLoading(true);
    try {
      const token = await getIdToken();
      const response = await fetch(`${API_BASE_URL}/api/resend-failed-emails`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          limit: parsedLimit,
          delayMs: parsedDelayMs,
          force: Boolean(resendForce),
        }),
      });

      const data = await response.json();

      if (response.ok) {
        showNotification("success", data.message || `Resend queued: ${data.queued || 0} logs`);
        setTimeout(() => {
          fetchEmailLogs();
          fetchServerStats();
        }, 2000);
      } else {
        const indexUrl = data?.indexUrl || String(data?.error || data?.message || "").match(/https:\/\/console\.firebase\.google\.com\/[^\s]+/)?.[0];
        if (indexUrl) {
          console.error("[FIRESTORE] Create composite index:", indexUrl);
        }
        showNotification("error", data.error || data.message || "Failed to start resend");
      }
    } catch (e) {
      console.error("Resend failed emails error:", e);
      showNotification("error", "Failed to start resend: " + e.message);
    } finally {
      setResendLoading(false);
    }
  };

  // Calculate Email Stats (Derived from serverStats if available)
  // Calculate Email Stats (Derived from serverStats if available)
  const emailStats = React.useMemo(() => {
    // 1. Initialize with ALL templates (except Racepack/Broadcast)
    const byType = {};
    Object.values(EMAIL_TEMPLATES).forEach(t => {
        // Exclude Racepack and Broadcast from the visible stats list per user request
        if (t.name && t.name !== "Racepack" && t.name !== "Broadcast") {
            byType[t.name] = 0;
        }
    });

    // 2. Merge Server Stats or Logs
    if (serverStats) {
        serverStats.distribution.forEach(item => {
            const typeName = getTemplateNameBySubject(item.subject);
            if (byType.hasOwnProperty(typeName)) {
                byType[typeName] += item.count;
            }
        });
    } else {
        // Fallback to client logs
        const logsToProcess = emailLogs || [];
        logsToProcess.forEach(log => {
            const typeName = getTemplateNameBySubject(log.subject);
            if (byType.hasOwnProperty(typeName)) {
                byType[typeName] += 1;
            }
        });
    }

    const total = serverStats ? serverStats.total : (totalLogs > 0 ? totalLogs : emailLogs.length);
    const topTypes = Object.entries(byType).sort(([, a], [, b]) => b - a);

    return { total, topTypes };
  }, [emailLogs, totalLogs, serverStats]);

  const [selectedTemplate, setSelectedTemplate] = useState("success");
  const [templateSubject, setTemplateSubject] = useState(EMAIL_TEMPLATES.success.subject);
  const [templateBody, setTemplateBody] = useState(EMAIL_TEMPLATES.success.body);
  const [testEmail, setTestEmail] = useState("");
  const [sendingTest, setSendingTest] = useState(false);
  const [selectedLog, setSelectedLog] = useState(null); 
  const [retryLoading, setRetryLoading] = useState(false);
  
  // Custom Variables for Racepack
  const [rpcDate, setRpcDate] = useState("");
  const [rpcLocation, setRpcLocation] = useState("");

  useEffect(() => {
    fetchParticipants();
    fetchServerStats(); 
    fetchTargetCounts(); // Fetch counts for smart targeting
  }, []);

  const fetchTargetCounts = async () => {
    try {
        const pRef = collection(db, COLLECTION_NAME);
        const [allSnap, verifiedSnap, pendingSnap] = await Promise.all([
            getCountFromServer(pRef),
            getCountFromServer(query(pRef, where("transaction_status", "in", ["settlement", "capture"]))),
            getCountFromServer(query(pRef, where("transaction_status", "==", "pending")))
        ]);
        setTargetCounts({
            all: allSnap.data().count,
            verified: verifiedSnap.data().count,
            pending: pendingSnap.data().count
        });
    } catch (e) {
        console.error("Error fetching target counts:", e);
    }
  };

  useEffect(() => {
    if (activeTab === "logs") {
      fetchEmailLogs();
      fetchServerStats(); // Refresh stats when looking at logs
    }
  }, [activeTab]);

  // Update filtered participants when search changes
  useEffect(() => {
    if (!searchQuery) {
        setFilteredParticipants(participants);
    } else {
        const lower = searchQuery.toLowerCase();
        const filtered = participants.filter(p => 
            (p.namaLengkap || "").toLowerCase().includes(lower) || 
            (p.email || "").toLowerCase().includes(lower) ||
            (p.orderId || "").toLowerCase().includes(lower)
        );
        setFilteredParticipants(filtered);
    }
  }, [searchQuery, participants]);

  // Update template content when selection changes (Fetch from Firestore)
  useEffect(() => {
    const fetchTemplate = async () => {
        const defaultTemplate = EMAIL_TEMPLATES[selectedTemplate];
        if (!defaultTemplate) return;

        // Set defaults first (instant feedback)
        setTemplateSubject(defaultTemplate.subject);
        setTemplateBody(defaultTemplate.body);

        try {
            // Check if overridden in Firestore
            const docRef = doc(db, "emailTemplates", selectedTemplate);
            const docSnap = await getDoc(docRef);

            if (docSnap.exists()) {
                const data = docSnap.data();
                setTemplateSubject(data.subject);
                setTemplateBody(data.body);
            }
        } catch (error) {
            console.error("Error fetching template override:", error);
        }
    };

    fetchTemplate();
  }, [selectedTemplate]);

  // Auto-expand {{collectionInfo}} into editable table for Racepack components
  useEffect(() => {
     if (selectedTemplate === "racepack" && templateBody.includes("{{collectionInfo}}")) {
        const expandedTable = `
<table class="detail-table">
  <tr><td class="detail-label">Date</td><td class="detail-value">[EDIT DATE HERE]</td></tr>
  <tr><td class="detail-label">Location</td><td class="detail-value">[EDIT LOCATION HERE]</td></tr>
  <tr><td class="detail-label">Time</td><td class="detail-value">08:00 - 17:00 WIB</td></tr>
</table>`;
        setTemplateBody(prev => prev.replace("{{collectionInfo}}", expandedTable));
     }
  }, [templateBody, selectedTemplate]);

  const fetchParticipants = async () => {
    try {
      // Fetch all participants as requested by user
      const q = query(collection(db, COLLECTION_NAME));
      const snapshot = await getDocs(q);
      const list = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
      setParticipants(list);
      setFilteredParticipants(list);
    } catch (error) {
      console.error("Error fetching participants:", error);
    } finally {
      setLoading(false);
    }
  };

  // Save template to Firestore
  const saveTemplate = async () => {
    if (!templateSubject || !templateBody) {
        showNotification("error", "Subject and Body cannot be empty");
        return;
    }

    try {
        setLoading(true); 
        await setDoc(doc(db, "emailTemplates", selectedTemplate), {
            subject: templateSubject,
            body: templateBody,
            updatedAt: serverTimestamp(),
            name: EMAIL_TEMPLATES[selectedTemplate].name, 
            variables: EMAIL_TEMPLATES[selectedTemplate].variables
        }, { merge: true });
        
        showNotification("success", "Template saved successfully! Backend will use this version.");
    } catch (error) {
        console.error("Error saving template:", error);
        showNotification("error", "Failed to save template");
    } finally {
        setLoading(false);
    }
  };


  // Selection Logic
  const toggleSelection = (id) => {
    setSelectedIds(prev => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        return next;
    });
  };

  const handleSelectAll = () => {
    // Select all CURRENTLY FILTERED participants
    const allIds = filteredParticipants.map(p => p.id);
    setSelectedIds(new Set(allIds));
  };

  const handleClearSelection = () => {
    setSelectedIds(new Set());
  };

  const getRecipientCount = () => selectedIds.size;

  // Helper for consistent sample data replacement
  const replaceWithSampleData = (text, type = "default") => {
    // Helper for HTML cleanup (removes newlines and extra spaces between tags)
    const cleanHtmlFragment = (html) => html.replace(/>\s+</g, "><").replace(/\n/g, "").trim();

    // Use user input if available, otherwise clear placeholders
    const displayDate = rpcDate || "[DATE FILLED BY ADMIN]";
    const displayLocation = rpcLocation || "[LOCATION FILLED BY ADMIN]";

    // Generate specific table for the type
    let registrationInfo = "";
    let collectionInfo = ""; // New variable for collection info
    if (type === "success") {
      registrationInfo = cleanHtmlFragment(`
        <table class="detail-table">
          <tr><td class="detail-label">Order ID</td><td class="detail-value">JOTR-2026-001234</td></tr>
          <tr><td class="detail-label">Category</td><td class="detail-value">21K Trail Run</td></tr>
          <tr><td class="detail-label">Price</td><td class="detail-value"><strong>Rp 350,000</strong></td></tr>
          <tr><td class="detail-label">Status</td><td class="detail-value"><span class="badge badge-paid">COMPLETED</span></td></tr>
        </table>
      `);
    } else if (type === "pending") {
      registrationInfo = cleanHtmlFragment(`
        <table class="detail-table">
          <tr><td class="detail-label">Order ID</td><td class="detail-value">JOTR-2026-001234</td></tr>
          <tr><td class="detail-label">Category</td><td class="detail-value">21K Trail Run</td></tr>
          <tr><td class="detail-label">Status</td><td class="detail-value"><span class="badge badge-pending">PENDING VERIFICATION</span></td></tr>
        </table>
      `);
    } else if (type === "approved") {
      registrationInfo = cleanHtmlFragment(`
        <table class="detail-table">
          <tr><td class="detail-label">Order ID</td><td class="detail-value">JOTR-2026-001234</td></tr>
          <tr><td class="detail-label">Category</td><td class="detail-value">21K Trail Run</td></tr>
          <tr><td class="detail-label">Status</td><td class="detail-value"><span class="badge badge-qualified">QUALIFIED</span></td></tr>
        </table>
      `);
    } else if (type === "rejected") {
      registrationInfo = cleanHtmlFragment(`
        <table class="detail-table">
          <tr><td class="detail-label">Order ID</td><td class="detail-value">JOTR-2026-001234</td></tr>
          <tr><td class="detail-label">Category</td><td class="detail-value">21K Trail Run</td></tr>
          <tr><td class="detail-label">Status</td><td class="detail-value"><span class="badge badge-rejected">REJECTED</span></td></tr>
        </table>
      `);
    } else if (type === "expired") {
      registrationInfo = cleanHtmlFragment(`
        <table class="detail-table">
          <tr><td class="detail-label">Order ID</td><td class="detail-value">JOTR-2026-001234</td></tr>
          <tr><td class="detail-label">Category</td><td class="detail-value">21K Trail Run</td></tr>
          <tr><td class="detail-label">Status</td><td class="detail-value"><span class="badge badge-rejected">EXPIRED</span></td></tr>
        </table>
      `);
    } else if (type === "racepack" || type === "Ricepack" || message.includes("{{collectionInfo}}")) { 
      // Using broadcheck for racepack preview
      registrationInfo = cleanHtmlFragment(`
        <table class="detail-table">
          <tr><td class="detail-label">Order ID</td><td class="detail-value">JOTR-2026-001234</td></tr>
          <tr><td class="detail-label">Category</td><td class="detail-value">21K Trail Run</td></tr>
        </table>
      `);
      collectionInfo = cleanHtmlFragment(`
        <table class="detail-table">
           <tr><td class="detail-label">Date</td><td class="detail-value">${displayDate}</td></tr>
           <tr><td class="detail-label">Location</td><td class="detail-value">${displayLocation}</td></tr>
           <tr><td class="detail-label">Time</td><td class="detail-value">08:00 - 17:00 WIB</td></tr>
        </table>
      `);
    } else if (type === "shuttle_success") {
      registrationInfo = cleanHtmlFragment(`
        <table class="detail-table">
          <tr><td class="detail-label">Order ID</td><td class="detail-value">SHUTTLE-123456</td></tr>
          <tr><td class="detail-label">Trip Type</td><td class="detail-value">Round Trip</td></tr>
          <tr><td class="detail-label">Departure</td><td class="detail-value">Jakarta Pusat</td></tr>
          <tr><td class="detail-label">Departure Schedule</td><td class="detail-value">27 Sept 2026, 09:00 WIB</td></tr>
          <tr><td class="detail-label">Return</td><td class="detail-value">Jakarta Pusat</td></tr>
          <tr><td class="detail-label">Return Schedule</td><td class="detail-value">28 Sept 2026, 19:00 WIB</td></tr>
          <tr><td class="detail-label">Total Paid</td><td class="detail-value"><strong>Rp 200,000</strong></td></tr>
          <tr><td class="detail-label">Status</td><td class="detail-value"><span class="badge badge-paid">COMPLETED</span></td></tr>
        </table>
      `);
    } else if (type === "shuttle_payment_instruction") {
      registrationInfo = cleanHtmlFragment(`
        <table class="detail-table">
          <tr><td class="detail-label">Order ID</td><td class="detail-value">SHUTTLE-123456</td></tr>
          <tr><td class="detail-label">Trip Type</td><td class="detail-value">Round Trip</td></tr>
          <tr><td class="detail-label">Total Invoice</td><td class="detail-value"><strong>Rp 200,000</strong></td></tr>
          <tr><td class="detail-label">Status</td><td class="detail-value"><span class="badge badge-pending">PAYMENT PENDING</span></td></tr>
        </table>
      `);
    } else if (type === "shuttle_expired") {
      registrationInfo = cleanHtmlFragment(`
        <table class="detail-table">
          <tr><td class="detail-label">Order ID</td><td class="detail-value">SHUTTLE-123456</td></tr>
          <tr><td class="detail-label">Trip Type</td><td class="detail-value">Round Trip</td></tr>
          <tr><td class="detail-label">Status</td><td class="detail-value"><span class="badge badge-rejected">EXPIRED</span></td></tr>
        </table>
      `);
    } else {
       // Default generic table
       registrationInfo = cleanHtmlFragment(`
        <table class="detail-table">
          <tr><td class="detail-label">Order ID</td><td class="detail-value">JOTR-2026-001234</td></tr>
          <tr><td class="detail-label">Category</td><td class="detail-value">21K Trail Run</td></tr>
        </table>
      `);
    }

    const sampleData = {
      "{{name}}": "John Doe",
      "{{nama}}": "John Doe", // Legacy
      "{{orderId}}": "JOTR-2026-001234",
      "{{category}}": "21K Trail Run",
      "{{kategori}}": "21K Trail Run", // Legacy
      "{{price}}": "Rp 350,000",
      "{{harga}}": "Rp 350,000", // Legacy
      "{{expiryTime}}": "5 January 2026, 23:59 WIB",
      "{{tanggal}}": displayDate,
      "{{lokasi}}": displayLocation,
      "{{bibNumber}}": "JOTR-2026-001",
      "{{status}}": "QUALIFIED",
      "{{paymentInfo}}": cleanHtmlFragment(`
        <table class="detail-table">
          <tr><td class="detail-label">BNI Virtual Account</td><td class="detail-value">8578735294443073</td></tr>
          <tr><td class="detail-label">Total Invoice</td><td class="detail-value"><strong>Rp 855,000</strong></td></tr>
          <tr><td class="detail-label">Deadline</td><td class="detail-value">24 Jan 2026, 12:03 WIB</td></tr>
        </table>
      `),
      "{{registrationInfo}}": registrationInfo,
      "{{shuttleInfo}}": registrationInfo,
      "{{shuttleDetail}}": registrationInfo,
      "{{collectionInfo}}": collectionInfo // Assuming racepack uses collectionInfo variable mapped to the generated table
    };

    let result = text || "";
    Object.keys(sampleData).forEach((key) => {
      // Use replace with a function to handle potential special chars in replacement
      result = result.split(key).join(sampleData[key]);
    });
    return result;
  };

  const generatePreviewHtml = (recipientName) => {
    return generateTemplateHtml(recipientName, replaceWithSampleData(message, "racepack"), "default");
  };

  // Replace template variables with sample data
  const getSampleTemplatePreview = () => {
    return replaceWithSampleData(templateBody, selectedTemplate);
  };

  const handleSendTestEmail = async () => {
    if (!testEmail.trim()) {
      showNotification("error", "Enter an email address for the test");
      return;
    }

    setSendingTest(true);

    try {
      const response = await fetch(`${API_BASE_URL}/api/send-email`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          to: testEmail,
          name: "Test User",
          subject: templateSubject,
          text: getSampleTemplatePreview(),
        }),
      });

      const result = await response.json();

      if (response.ok) {
        showNotification("success", `✅ Test email sent to ${testEmail}`);
      } else {
        showNotification("error", result.error || "Failed to send test email");
      }
    } catch (error) {
      console.error("Error sending test email:", error);
      showNotification("error", "Failed to send test email");
    } finally {
      setSendingTest(false);
    }
  };

  const handleSendBulk = async () => {
    if (!subject.trim() || !message.trim()) {
      showNotification("error", "Subject and message are required");
      return;
    }

    if (broadcastTarget === "none" && selectedIds.size === 0) {
      showNotification("error", "Please select recipients or a target group");
      return;
    }

    const recipientDesc = broadcastTarget !== "none" 
        ? `all ${broadcastTarget} participants` 
        : `${selectedIds.size} selected recipients`;

    if (!confirm(`Send email to ${recipientDesc}?`)) return;

    setSending(true);

    try {
      const token = await getIdToken();
      let response;

      if (broadcastTarget !== "none") {
        // High Efficiency Server-Side Broadcast
        response = await fetch(`${API_BASE_URL}/api/send-broadcast`, {
            method: "POST",
            headers: { 
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`
            },
            body: JSON.stringify({
                target: broadcastTarget,
                subject,
                text: message,
                eventId: "JOTR2026",
                variables: {
                    tanggal: rpcDate,
                    lokasi: rpcLocation
                }
            }),
        });
      } else {
        // Legacy Client-Side Selection
        const recipients = participants
            .filter(p => selectedIds.has(p.id))
            .map((p) => ({
                email: p.email,
                name: p.namaLengkap || p.teamName || "Participant",
            }));

        response = await fetch(`${API_BASE_URL}/api/send-bulk-email`, {
            method: "POST",
            headers: { 
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`
            },
            body: JSON.stringify({
                recipients: recipients,
                subject,
                text: message,
                eventName: "Jakarta Open Trail Run 2026",
                variables: {
                    tanggal: rpcDate,
                    lokasi: rpcLocation
                }
            }),
        });
      }

      const result = await response.json();

      if (response.ok) {
        if (broadcastTarget !== "none") {
            showNotification("success", `🚀 Broadcast started! ${result.message}`);
        } else {
            showNotification("success", `✅ Email sent: ${result.successCount} success, ${result.failCount || 0} failed`);
        }
        
        setSubject("");
        setMessage("");
        handleClearSelection(); 
        setBroadcastTarget("none");
        
        // Refresh logs after a short delay since broadcast is background
        setTimeout(() => {
            fetchEmailLogs();
            fetchServerStats();
        }, 2000);

      } else {
        showNotification("error", result.error || "Failed to send emails");
      }
    } catch (error) {
      console.error("Error sending emails:", error);
      showNotification("error", "Failed to send emails: " + error.message);
    } finally {
      setSending(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleString("en-US", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getStatusBadge = (status) => {
     switch (status) {
      case "sent": return <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-800"><LuCheck /> Sent</span>;
      case "failed": return <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-800"><LuX /> Failed</span>;
      case "partial": return <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-yellow-100 text-yellow-800"><LuInfo /> Partial</span>;
      default: return <span className="text-slate-500">{status}</span>;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LuLoader className="animate-spin text-4xl text-slate-800" />
      </div>
    );
  }

  return (
    <div className="space-y-4 pb-20">
      {/* Header */}
      <div className="bg-white rounded-xl p-4 border border-slate-200 text-slate-800 relative overflow-hidden">
         <div className="absolute top-0 right-0 p-4 opacity-5 text-slate-200">
            <LuMail size={120} />
         </div>
         <div className="relative z-10">
            <p className="text-slate-500 font-medium mb-1 flex items-center gap-2 text-xs uppercase tracking-wider">
               <LuSend size={14} /> Broadcast
            </p>
            <div className="flex justify-between items-end">
                <div>
                    <h1 className="text-3xl font-bold mb-2">Email Center</h1>
                    <p className="text-slate-500 text-sm max-w-xl">
                        Manage broadcast emails and track sending history.
                    </p>
                </div>
            </div>
         </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden mb-6">
        <div className="flex border-b">
          <button
            onClick={() => setActiveTab("compose")}
            className={`flex-1 py-4 px-4 text-sm font-semibold transition-colors flex items-center justify-center gap-2 ${
              activeTab === "compose"
                ? "bg-slate-100 text-slate-800"
                : "text-slate-500 hover:bg-slate-50"
            }`}
          >
            <LuMail size={18} />
            <span>Compose Broadcast</span>
          </button>
          <button
            onClick={() => setActiveTab("templates")}
            className={`flex-1 py-4 px-4 text-sm font-semibold transition-colors flex items-center justify-center gap-2 ${
              activeTab === "templates"
                ? "bg-slate-100 text-slate-800"
                : "text-slate-500 hover:bg-slate-50"
            }`}
          >
            <LuLayoutTemplate size={18} />
            <span>Templates</span>
          </button>
          <button
            onClick={() => setActiveTab("logs")}
            className={`flex-1 py-4 px-4 text-sm font-semibold transition-colors flex items-center justify-center gap-2 ${
              activeTab === "logs"
                ? "bg-slate-100 text-slate-800"
                : "text-slate-500 hover:bg-slate-50"
            }`}
          >
            <LuHistory size={18} />
            <span>Email Logs</span>
          </button>
        </div>
      </div>

      {/* Main Content Area */}
      {activeTab === "compose" && (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
            
            {/* LEFT COLUMN: Participant Selection (Span 4) */}
            <div className="lg:col-span-4 h-full">
                <div className="bg-white rounded-xl border border-slate-200 p-5 h-full flex flex-col shadow-sm">
                    <h3 className="font-bold text-slate-800 mb-6 flex items-center justify-between">
                        <span>Select Participants</span>
                        <span className="text-[10px] bg-slate-50 px-2 py-0.5 rounded-md text-slate-400 font-bold uppercase tracking-wider">
                            {participants.length} Total
                        </span>
                    </h3>
                    
                    {/* Broad Targeting (Neutral Style) */}
                    <div className="mb-4">
                        <p className="text-[10px] font-bold uppercase text-slate-300 tracking-widest mb-3">Targeting Groups</p>
                        <div className="space-y-1.5">
                            {[
                                { id: "none", label: "Manual Selection", count: selectedIds.size },
                                { id: "verified", label: "All Verified", count: targetCounts.verified },
                                { id: "pending", label: "All Pending", count: targetCounts.pending },
                                { id: "all", label: "All Registered", count: targetCounts.all },
                            ].filter(t => t.id === "none" || t.count > 0).map((t) => (
                                <button
                                    key={t.id}
                                    onClick={() => {
                                        setBroadcastTarget(t.id);
                                        if (t.id !== "none") setSelectedIds(new Set());
                                    }}
                                    className={`w-full flex items-center justify-between p-3.5 rounded-xl border transition-all ${
                                        broadcastTarget === t.id 
                                        ? "bg-slate-50 border-slate-300 ring-1 ring-slate-100" 
                                        : "bg-white border-slate-100 text-slate-500 hover:border-slate-200 hover:bg-slate-50/30"
                                    }`}
                                >
                                    <span className={`text-sm font-semibold ${broadcastTarget === t.id ? "text-slate-900" : "text-slate-600"}`}>
                                        {t.label}
                                    </span>
                                    <span className={`text-[11px] font-bold px-2 py-0.5 rounded-md ${
                                        broadcastTarget === t.id ? "bg-slate-200 text-slate-700" : "bg-slate-50 text-slate-400"
                                    }`}>
                                        {t.count.toLocaleString()}
                                    </span>
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="border-t border-slate-50" />

                    {/* Manual / Search (Neutral Style) */}
                    <div className={`flex flex-col flex-1 min-h-0 transition-all duration-300 ${broadcastTarget !== "none" ? "opacity-20 pointer-events-none grayscale" : "opacity-100"}`}>
                        <div className="mb-4">
                            <InputField
                                name="search"
                                placeholder="Search by name, email, or order ID..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                labelClassName="sr-only"
                                className="bg-slate-50/50 border-slate-100"
                            />
                        </div>

                        <div className="flex gap-2 mb-4">
                            <button 
                                onClick={handleSelectAll}
                                className="flex-1 py-1.5 text-[10px] font-bold border border-slate-100 rounded-lg hover:bg-slate-50 transition-colors uppercase tracking-widest text-slate-400"
                            >
                                Select All ({filteredParticipants.length})
                            </button>
                            <button 
                                onClick={handleClearSelection}
                                className="flex-1 py-1.5 text-[10px] font-bold border border-slate-100 rounded-lg hover:bg-slate-50 transition-colors uppercase tracking-widest text-red-300 hover:text-red-400"
                            >
                                Clear
                            </button>
                        </div>

                        {/* List */}
                        <div className="flex-1 overflow-y-auto max-h-[400px] space-y-1.5 pr-1 custom-scrollbar">
                            {filteredParticipants.map(p => {
                                const isSelected = selectedIds.has(p.id);
                                return (
                                    <div 
                                        key={p.id}
                                        onClick={() => toggleSelection(p.id)}
                                        className={`p-3 rounded-xl border cursor-pointer transition-all flex items-center gap-3 ${
                                            isSelected 
                                            ? "bg-slate-50 border-slate-300" 
                                            : "bg-white border-slate-100 hover:border-slate-200"
                                        }`}
                                    >
                                        <div className={`w-3.5 h-3.5 rounded border flex items-center justify-center flex-shrink-0 ${
                                            isSelected ? "bg-slate-800 border-slate-800" : "border-slate-300 bg-white"
                                        }`}>
                                            {isSelected && <LuCheck size={8} className="text-white" />}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className={`text-[13px] font-semibold truncate ${isSelected ? "text-slate-900" : "text-slate-600"}`}>
                                                {p.namaLengkap || p.teamName || "Untitled"}
                                            </p>
                                            <p className="text-[10px] text-slate-400 truncate font-mono">{p.email}</p>
                                        </div>
                                        {p.transaction_status === "settlement" && (
                                            <div className="w-1 h-1 rounded-full bg-emerald-400" />
                                        )}
                                    </div>
                                );
                            })}
                            
                            {filteredParticipants.length === 0 && (
                                <div className="text-center py-10 opacity-30">
                                    <p className="text-xs">No records found.</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* RIGHT COLUMN: Composer (Span 8) */}
            <div className="lg:col-span-8 h-full"> 
                <div className="bg-white rounded-xl border border-slate-200 p-4 h-full flex flex-col">
                    <div className="mb-4 flex items-center justify-between flex-shrink-0">
                         <h3 className="font-semibold text-slate-800 text-lg">Compose Message</h3>
                          <div className="text-sm font-medium text-slate-600 bg-slate-100 px-3 py-1 rounded-full">
                            {broadcastTarget !== "none" 
                                ? `Targeting: All ${broadcastTarget}` 
                                : `${selectedIds.size} recipient(s) selected`
                            }
                          </div>
                    </div>

                    <div className="space-y-4 flex-1 flex flex-col">
                        {/* Subject */}
                        <div className="flex-shrink-0">
                            <InputField
                                label="Subject"
                                name="subject"
                                value={subject}
                                onChange={(e) => setSubject(e.target.value)}
                                placeholder="e.g. Important Information - JOTR 2026"
                                labelColor="text-slate-700"
                                labelClassName="font-medium mb-1"
                            />
                        </div>

                        {/* Body */}
                        <div className="flex-1 flex flex-col min-h-0">
                            <div className="flex-1 flex flex-col">
                                <InputField
                                    type="textarea"
                                    label="Message Body"
                                    name="message"
                                    value={message}
                                    onChange={(e) => setMessage(e.target.value)}
                                    placeholder="Write your message here... HTML tags are supported but keep it simple."
                                    rows={12}
                                    labelColor="text-slate-700"
                                    labelClassName="font-medium mb-1"
                                    className="h-full min-h-[200px]" 
                                />
                            </div>
                            <p className="text-xs text-slate-400 mt-2 text-right flex-shrink-0">
                                Header and Footer will be added automatically.
                            </p>
                        </div>
                        
                        {/* Racepack Dynamic Inputs */}
                        {(subject.toLowerCase().includes("racepack") || message.includes("{{collectionInfo}}")) && (
                            <div className="bg-slate-50 p-3 rounded-lg border border-slate-200 grid grid-cols-2 gap-3 mt-4">
                                <InputField
                                    label="Collection Date"
                                    name="rpcDate"
                                    value={rpcDate}
                                    onChange={(e) => setRpcDate(e.target.value)}
                                    placeholder="Example: 10 January 2026"
                                    labelClassName="text-xs font-bold uppercase text-slate-500 mb-1"
                                    className="text-sm"
                                />
                                <InputField
                                    label="Collection Location"
                                    name="rpcLocation"
                                    value={rpcLocation}
                                    onChange={(e) => setRpcLocation(e.target.value)}
                                    placeholder="Example: Cibodas, West Java"
                                    labelClassName="text-xs font-bold uppercase text-slate-500 mb-1"
                                    className="text-sm"
                                />
                            </div>
                        )}
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-4 mt-4 pt-4 border-t flex-shrink-0">
                        <button
                            onClick={() => setShowPreview(true)}
                            className="px-6 py-3 rounded-xl border border-slate-300 text-slate-600 font-medium hover:bg-slate-50 transition-colors flex items-center gap-2"
                        >
                            <LuEye /> Preview Email
                        </button>
                        
                        <Button
                            variant="none"
                            onClick={handleSendBulk}
                            disabled={sending || (broadcastTarget === "none" && selectedIds.size === 0)}
                            className={`flex-1 flex items-center justify-center gap-2 bg-slate-800 hover:bg-slate-900 text-white ${
                                sending || (broadcastTarget === "none" && selectedIds.size === 0)
                                    ? "bg-slate-400 cursor-not-allowed hover:bg-slate-400"
                                    : ""
                            }`}
                        >
                            {sending ? (
                            <>
                                <LuLoader className="animate-spin" />
                                Sending...
                            </>
                            ) : (
                            <>
                                <LuSend />
                                {broadcastTarget !== "none" ? `Send Broadcast to ${broadcastTarget}` : `Send to ${selectedIds.size} Recipients`}
                            </>
                            )}
                        </Button>
                    </div>


                </div>
            </div>
        </div>
      )}

      {/* ==================== TEMPLATES TAB (Legacy) ==================== */}
      {activeTab === "templates" && (
        <div className="space-y-4">
           {/* Template Selector */}
             <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2">
            {Object.entries(EMAIL_TEMPLATES).map(([key, template]) => (
                <button
                key={key}
                onClick={() => setSelectedTemplate(key)}
                className={`p-2 h-14 rounded-xl border border-slate-200 flex flex-col items-center justify-center text-center transition-colors font-sans ${
                    selectedTemplate === key
                    ? "bg-slate-100 text-slate-800 shadow-sm"
                    : "bg-white hover:bg-slate-50"
                }`}
                >
                <div className="flex flex-col items-center justify-center">
                    <p className={`text-[10px] font-bold uppercase tracking-tight leading-tight ${selectedTemplate === key ? "text-slate-800" : "text-slate-600"}`}>
                        {template.name}
                    </p>
                </div>
                </button>
            ))}
            </div>

            {/* Edit Template */}
            <div className="grid md:grid-cols-2 gap-4">
            {/* Editor */}
            <div className="space-y-4 h-full flex flex-col">
                <h3 className="font-semibold text-slate-800 flex items-center gap-2">
                <LuFileText />
                Edit Template
                </h3>

                {/* Variables */}
                <div className="bg-slate-50 rounded-xl p-3 border">
                <p className="text-xs font-medium text-slate-500 mb-2">Available Variables:</p>
                <div className="flex flex-wrap gap-1">
                    {EMAIL_TEMPLATES[selectedTemplate].variables.map((v) => (
                    <span
                        key={v}
                        className="bg-white px-2 py-0.5 rounded text-xs font-mono text-slate-600 border"
                    >
                        {v}
                    </span>
                    ))}
                </div>
                </div>

                {/* Subject */}
                <InputField
                    label="Subject"
                    name="templateSubject"
                    value={templateSubject}
                    onChange={(e) => setTemplateSubject(e.target.value)}
                    labelColor="text-slate-700"
                    labelClassName="font-medium mb-1"
                />

                {/* Body */}
                <div className="flex-1">
                    <InputField
                        type="textarea"
                        label="Body"
                        name="templateBody"
                        value={templateBody}
                        onChange={(e) => setTemplateBody(e.target.value)}
                        className="h-full min-h-[350px] font-mono text-sm leading-relaxed"
                        style={{ fontFamily: 'monospace' }}
                        labelColor="text-slate-700"
                        labelClassName="font-medium mb-1"
                    />
                </div>

                {/* Actions */}
                <div className="flex justify-end pt-2">
                    <Button
                        variant="none"
                        type="button"
                        onClick={saveTemplate}
                        disabled={loading}
                        className={`flex items-center gap-2 bg-slate-800 hover:bg-slate-900 text-white ${
                            loading ? "bg-slate-400 cursor-not-allowed hover:bg-slate-400" : ""
                        }`}
                    >
                        {loading ? <LuLoader className="animate-spin" /> : <LuSend className="rotate-0" />}
                        Save Changes (Live)
                    </Button>
                </div>
            </div>

            {/* Preview */}
            <div className="space-y-4 h-full flex flex-col">
                <h3 className="font-semibold text-slate-800 flex items-center gap-2">
                <LuEye />
                Live Preview
                </h3>
                <div className="border rounded-xl overflow-hidden flex flex-col flex-1 h-full min-h-[600px] shadow-sm bg-slate-50">
                    <div className="bg-white px-4 py-3 text-xs font-semibold text-slate-500 border-b flex justify-between items-center shadow-sm z-10">
                        <span className="truncate max-w-[300px]" title={templateSubject}>
                            Subject: <span className="text-slate-800">{templateSubject}</span>
                        </span>
                        <span className="bg-slate-100 text-slate-500 px-2 py-0.5 rounded text-[10px] uppercase tracking-wider">
                            HTML Preview
                        </span>
                    </div>
                    <div className="flex-1 relative bg-gray-100">
                        <div className="absolute inset-0 overflow-y-auto custom-scrollbar p-6">
                            <div
                                className="bg-white shadow-sm mx-auto max-w-[600px] min-h-full"
                                dangerouslySetInnerHTML={{
                                    __html: generateTemplateHtml("John Doe", getSampleTemplatePreview(), selectedTemplate),
                                }}
                            />
                        </div>
                    </div>
                </div>
            </div>
            </div>
        </div>
      )}

      {/* ==================== LOGS TAB ==================== */}
      {activeTab === "logs" && (
        <div className="space-y-4">
           {/* Statistics Cards */}
           {emailStats && (
             <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
               {/* Total Emails Card */}
               <div className="bg-white p-4 rounded-xl border border-slate-200">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                        <p className="text-slate-500 text-sm font-medium">Total Sent</p>
                        <h3 className="text-3xl font-bold text-slate-800">{emailStats.total}</h3>
                    </div>
                    <div className="text-slate-400 bg-slate-50 p-2 rounded-lg">
                        <LuSend size={20} />
                    </div>
                  </div>
                  <div className="text-xs text-slate-400">
                    Total messages recorded
                  </div>
               </div>

                {/* Email Distribution Card (Spans 2 columns) */}
                <div className="md:col-span-2 bg-white p-4 rounded-xl border border-slate-200">
                    <p className="text-slate-500 text-sm font-medium mb-3">Email Distribution</p>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-x-4 gap-y-2">
                      {emailStats.topTypes.map(([type, count], idx) => (
                        <div key={idx} className="flex justify-between items-center text-sm border-b border-slate-50 py-1 last:border-0">
                            <span className="truncate flex-1 pr-2 text-slate-700 font-medium flex items-center gap-2">
                                <span className={`w-2 h-2 rounded-full ${
                                    type.includes("Success") || type.includes("Approved") ? "bg-green-500" :
                                    type.includes("Pending") || type.includes("Payment") ? "bg-yellow-500" :
                                    type.includes("Rejected") || type.includes("Expired") ? "bg-red-500" : "bg-slate-300"
                                }`} />
                                {type}
                            </span>
                            <span className="font-bold text-slate-700 bg-slate-100 px-2.5 py-0.5 rounded-full text-xs">
                                {count}
                            </span>
                        </div>
                     ))}
                   </div>
               </div>
             </div>
           )}



            {/* Logs Table */}
            {logsLoading ? (
            <div className="flex items-center justify-center py-12">
                <LuLoader className="animate-spin text-3xl text-slate-800" />
            </div>
            ) : emailLogs.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
                <LuMail className="text-4xl mb-3 text-slate-300 mx-auto" />
                <p>No email logs yet</p>
            </div>
            ) : (
            <div className="overflow-hidden rounded-xl border border-slate-200 border-slate-200 bg-white">
                <table className="w-full">
                <thead>
                    <tr className="border-b border-slate-100 bg-slate-50/50">
                    <th className="text-left px-4 py-4 text-xs font-bold text-slate-400 uppercase tracking-wider w-[140px]">
                        Date
                    </th>
                    <th className="text-left px-4 py-4 text-xs font-bold text-slate-400 uppercase tracking-wider w-[240px]">
                        Recipient
                    </th>
                    <th className="text-left px-4 py-4 text-xs font-bold text-slate-400 uppercase tracking-wider">
                        Subject
                    </th>
                    <th className="text-center px-4 py-4 text-xs font-bold text-slate-400 uppercase tracking-wider w-[120px]">
                        Status
                    </th>
                    <th className="text-right px-4 py-4 text-xs font-bold text-slate-400 uppercase tracking-wider w-[180px]">
                        Message ID
                    </th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                    {emailLogs.map((log) => (
                    <tr 
                        key={log.id} 
                        onClick={() => setSelectedLog(log)}
                        className="hover:bg-slate-50 transition-colors cursor-pointer group"
                    >
                        <td className="px-4 py-4 text-xs text-slate-500 font-mono whitespace-nowrap">
                            {formatDate(log.createdAt)}
                        </td>
                        <td className="px-4 py-4">
                            <div className="flex flex-col w-full max-w-[220px]">
                                <span className="text-sm font-bold text-slate-800 truncate block" title={log.recipientName}>
                                    {log.recipientName || "Unknown"}
                                </span>
                                <span className="text-xs text-slate-400 truncate font-mono block" title={log.to}>
                                    {log.to}
                                </span>
                            </div>
                        </td>
                        <td className="px-4 py-4">
                            <div className="max-w-[300px] xl:max-w-[400px]">
                                <p className="text-sm font-bold text-slate-800 uppercase truncate" title={log.subject}>
                                    {log.subject
                                        .replace(/ - JOTR CIANJUR SERIES 2026/gi, "")
                                        .replace(/ - Jakarta Open Trail Run( 2026)?/gi, "")
                                    }
                                </p>
                            </div>
                        </td>
                        <td className="px-4 py-4 text-center">
                            <span className={`inline-flex items-center px-2.5 py-1 rounded-lg text-[10px] font-bold uppercase tracking-wide border ${
                                log.status === 'sent' || log.status === 'delivered' 
                                ? 'bg-green-50 text-green-700 border-green-100' 
                                : log.status === 'failed' 
                                ? 'bg-red-50 text-red-700 border-red-100' 
                                : 'bg-slate-50 text-slate-600 border-slate-100'
                            }`}>
                                {log.status === 'sent' ? 'DELIVERED' : log.status}
                            </span>
                        </td>
                        <td className="px-4 py-4 text-right">
                            <div className="flex flex-col items-end">
                                <span className="text-[10px] text-slate-400 font-mono truncate max-w-[150px]">
                                    {log.id}
                                </span>
                                {log.error && (
                                    <span className="text-[10px] text-red-500 truncate max-w-[150px]" title={log.error}>
                                        {log.error}
                                    </span>
                                )}
                            </div>
                        </td>
                    </tr>
                    ))}
                </tbody>
                </table>
            </div>
            )}
        </div>
      )}

      {/* Log Details Modal */}
      {selectedLog && (
        <div className="fixed inset-0 z-[1000] flex items-center justify-center p-4">
            <div 
                className="fixed inset-0 bg-black/40 backdrop-blur-sm" 
                onClick={() => setSelectedLog(null)}
            />
            <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden animate-scale-in">
                {/* Modal Header */}
                <div className="p-4 border-b border-slate-100 flex justify-between items-start">
                    <div>
                        <h3 className="text-xl font-bold text-slate-800">Email Details</h3>
                        <p className="text-xs text-slate-400 font-mono mt-1">&lt;{selectedLog.id || "ID-NOT-FOUND"}&gt;</p>
                    </div>
                    <button 
                        onClick={() => setSelectedLog(null)}
                        className="text-slate-400 hover:text-slate-600 p-1 rounded-lg hover:bg-slate-100 transition-colors"
                    >
                        <LuX size={20} />
                    </button>
                </div>

                {/* Modal Body */}
                <div className="p-4 overflow-y-auto space-y-4 bg-white">
                    {/* Meta Grid */}
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-1">Sent At</p>
                            <p className="text-sm font-medium text-slate-700">{formatDate(selectedLog.createdAt)}</p>
                        </div>
                        <div>
                            <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-1">Status</p>
                            <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-bold uppercase ${
                                selectedLog.status === 'sent' || selectedLog.status === 'delivered' 
                                ? 'bg-green-100 text-green-700' 
                                : 'bg-red-100 text-red-700'
                            }`}>
                                {selectedLog.status === 'sent' ? 'DELIVERED' : selectedLog.status}
                            </span>
                        </div>
                    </div>

                    {/* Recipient Box */}
                    <div>
                        <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Recipient</p>
                        <div className="flex items-center gap-3 p-3 bg-slate-50 border border-slate-200 rounded-lg">
                            <div className="w-10 h-10 rounded-full bg-slate-200 flex items-center justify-center text-slate-500">
                                <LuUser size={20} />
                            </div>
                            <div>
                                <p className="text-sm font-bold text-slate-800">{selectedLog.recipientName || "Unknown"}</p>
                                <p className="text-xs text-slate-500 font-mono">{selectedLog.to}</p>
                            </div>
                        </div>
                    </div>

                     {/* Content Preview */}
                    <div>
                        <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Subject & Content</p>
                        <div className="border border-slate-200 rounded-lg overflow-hidden">
                             <div className="bg-slate-50 px-4 py-3 border-b border-slate-200">
                                <p className="text-sm font-bold text-slate-800">{selectedLog.subject}</p>
                             </div>
                             <div className="p-4 bg-white max-h-[500px] overflow-y-auto custom-scrollbar">
                                {selectedLog.text && selectedLog.text.includes("<!DOCTYPE html>") ? (
                                    <div dangerouslySetInnerHTML={{ 
                                        __html: selectedLog.text 
                                    }} />
                                ) : (
                                    <div className="border border-slate-200 rounded overflow-hidden">
                                        <div 
                                            className="bg-white"
                                            dangerouslySetInnerHTML={{ 
                                                __html: generateTemplateHtml(selectedLog.recipientName, selectedLog.text || selectedLog.preview || "No content.", 'default') 
                                            }}
                                        />
                                        <p className="text-xs text-slate-400 mt-2 p-2 bg-slate-50 text-center">Preview generated with current styles.</p>
                                    </div>
                                )}
                             </div>
                        </div>
                    </div>
                </div>

                {/* Modal Footer */}
                <div className="p-4 bg-slate-50 border-t border-slate-200 flex justify-end gap-3">
                    {selectedLog.status === 'failed' && (
                        <button 
                            onClick={handleRetryEmailLog}
                            disabled={retryLoading}
                            className={`px-4 py-2 text-white text-sm font-semibold rounded-lg transition-colors flex items-center gap-2 ${
                                retryLoading ? "bg-slate-300" : "bg-blue-600 hover:bg-blue-700"
                            }`}
                        >
                            {retryLoading ? (
                                <>
                                    <LuLoader className="animate-spin" />
                                    Retrying...
                                </>
                            ) : (
                                <>
                                    <LuRotateCcw />
                                    Retry
                                </>
                            )}
                        </button>
                    )}
                    <button 
                        onClick={() => setSelectedLog(null)}
                        className="px-4 py-2 bg-slate-800 hover:bg-slate-900 text-white text-sm font-semibold rounded-lg transition-colors"
                    >
                        Close
                    </button>
                </div>
            </div>
        </div>
      )}

      {/* Broadcast Preview Modal */}
      {showPreview && (
        <div className="fixed inset-0 z-[1000] flex items-center justify-center p-4">
            <div 
                className="fixed inset-0 bg-black/40 backdrop-blur-sm" 
                onClick={() => setShowPreview(false)}
            />
            <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden animate-scale-in">
                {/* Modal Header */}
                <div className="p-4 border-b border-slate-100 flex justify-between items-start">
                    <div>
                        <h3 className="text-xl font-bold text-slate-800">Broadcast Preview</h3>
                        <p className="text-xs text-slate-400 mt-1 uppercase tracking-wider font-bold">Preview Mode</p>
                    </div>
                    <button 
                        onClick={() => setShowPreview(false)}
                        className="text-slate-400 hover:text-slate-600 p-1 rounded-lg hover:bg-slate-100 transition-colors"
                    >
                        <LuX size={20} />
                    </button>
                </div>

                {/* Modal Body */}
                <div className="p-4 overflow-y-auto space-y-4 bg-white">
                    {/* Recipient Box (Sample) */}
                    <div>
                        <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Sample Recipient</p>
                        <div className="flex items-center gap-3 p-3 bg-slate-50 border border-slate-200 rounded-lg">
                            <div className="w-10 h-10 rounded-full bg-slate-200 flex items-center justify-center text-slate-500">
                                <LuUser size={20} />
                            </div>
                            <div>
                                <p className="text-sm font-bold text-slate-800">John Doe (Sample)</p>
                                <p className="text-xs text-slate-500 font-mono">john.doe@example.com</p>
                            </div>
                        </div>
                    </div>

                    {/* Content Preview */}
                    <div>
                        <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Preview Subject & Content</p>
                        <div className="border border-slate-200 rounded-lg overflow-hidden">
                             <div className="bg-slate-50 px-4 py-3 border-b border-slate-200">
                                <p className="text-sm font-bold text-slate-800">{subject || "(No Subject)"}</p>
                             </div>
                             <div className="p-4 bg-white max-h-[400px] overflow-y-auto custom-scrollbar">
                                <div dangerouslySetInnerHTML={{ __html: generatePreviewHtml("John Doe") }} />
                             </div>
                        </div>
                        <p className="text-[10px] text-slate-400 mt-3 text-center italic">
                            * This is a preview using sample data. Highlighting variables like {'{{nama}}'} or {'{{orderId}}'} as they will appear to recipients.
                        </p>
                    </div>
                </div>

                {/* Modal Footer */}
                <div className="p-4 bg-slate-50 border-t border-slate-200 flex justify-end gap-3">
                    <button 
                        onClick={() => setShowPreview(false)}
                        className="px-6 py-2 bg-slate-800 hover:bg-slate-900 text-white text-sm font-semibold rounded-lg transition-colors"
                    >
                        Close Preview
                    </button>
                </div>
            </div>
        </div>
      )}
    </div>
  );
};
